import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { formatDisplayText, formatValueSafely } from '../../utils/text-case.utils';

@Component({
  selector: 'app-filter-option-buttons',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './filter-option-buttons.component.html',
  styleUrl: './filter-option-buttons.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FilterOptionButtonsComponent),
      multi: true,
    },
  ],
})
export class FilterOptionButtonsComponent implements ControlValueAccessor {
  @Input() data: any;
  @Input() options: any[] = [];
  @Output() filterChanged = new EventEmitter<any>();

  selectedValues: any[] = [];

  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  constructor(private globalFilterService: GlobalFilterService) {}

  get label(): string {
    return this.formatLabel(this.data?.name || this.data?.label || this.data?.title || '');
  }

  get isMultiSelect(): boolean {
    return this.data?.isMultiSelect === true;
  }

  selectOption(option: any): void {
    if (this.isMultiSelect) {
      this.selectedValues = this.isSelected(option)
        ? this.selectedValues.filter((item) => item !== option)
        : [...this.selectedValues, option];
    } else if (this.isSelected(option)) {
      this.selectedValues = [];
    } else {
      this.selectedValues = [option];
    }

    const controlValue = this.isMultiSelect
      ? [...this.selectedValues]
      : (this.selectedValues[0] ?? null);

    this.onChangeFn(controlValue);
    this.onTouchedFn();

    const emittedValue = [...this.selectedValues];
    const filterListener = this.data?.filterListener || null;

    if (this.data?.key) {
      this.globalFilterService.activeFilter = this.data.key;

      if (emittedValue.length > 0) {
        this.globalFilterService.updateFilterSelection(
          this.data.key,
          emittedValue,
          this.data?.isQueryParam,
          undefined,
          true,
          filterListener,
        );
      } else {
        this.globalFilterService.resetFilterSelection(
          this.data.key,
          this.data?.isQueryParam,
          filterListener,
        );
      }
    }

    this.filterChanged.emit(emittedValue);
  }

  isSelected(option: any): boolean {
    return this.selectedValues.includes(option);
  }

  trackByValue(_: number, option: any): any {
    return option;
  }

  writeValue(value: any): void {
    if (value === null || value === undefined || value === '') {
      this.selectedValues = [];
      return;
    }

    this.selectedValues = Array.isArray(value) ? [...value] : [value];
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState?(_isDisabled: boolean): void {}

  formatLabel(value: unknown): string {
    const mode = (this.data?.textCase || 'capital') as any;
    return formatDisplayText(value, mode);
  }

  formatOption(value: unknown): string {
    const mode = (this.data?.textCase || 'capital') as any;
    return formatValueSafely(value, mode);
  }
}
